import { ChangeEvent, FC, useRef } from "react";
import { twMerge } from "tailwind-merge";

import { serverImportExternalSection } from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { FullSectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { LegacyTextField } from "mds/components/LegacyTextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentOrg } from "store/selectors";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

type AdminSettingsMainProps = {
  sections: FullSectionType[];
  readOnly?: boolean;
};

const onImportSection = async (event: ChangeEvent<HTMLInputElement>) => {
  event.stopPropagation();
  event.preventDefault();

  const file = event.target.files![0];

  const formData = new FormData();
  formData.append("data", file);

  const response = await serverImportExternalSection(formData);

  if (response) {
    toastSuccessMessage(t("home.settings.import_section_success"));
    trackEvent("Course imported", {
      ...GENERAL_EVENT_CATEGORY,
      course: response.course.code,
      courseTerm: response.term.title,
      sectionTitle: response.title,
    });
  }
};

export const AdminSettingsMain: FC<AdminSettingsMainProps> = ({ sections, readOnly = false }) => {
  const currentOrg = useAppSelector(selectCurrentOrg);
  const sectionInput = useRef<HTMLInputElement>(null);

  if (!sections || !currentOrg) {
    return <LoadingScreen>{t("home.settings.no_sections")}</LoadingScreen>;
  }

  const handleNameChange = (name: string) => {
    storeApi.orgs.partial_update({ id: currentOrg.id, name });
  };

  const handleAiAssessmentEnabledChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isEnabled = event.target.checked;
    storeApi.orgs.partial_update({
      id: currentOrg.id,
      ai_assessment_enabled: isEnabled,
    });
    trackEvent("Org Settings - AI assessment toggle", {
      ...BUTTON_EVENT_CATEGORY,
      isEnabled,
    });
  };

  return (
    <div>
      <label>
        <span className="h6 text-black-tint-40">{t("home.settings.institution_name")}</span>
        <LegacyTextField
          disabled={readOnly}
          value={currentOrg?.name}
          wrapperClassName="flex justify-start items-center"
          isEditing
          onChange={(event) => handleNameChange(event.target.value)}
        />
      </label>

      <label
        className={twMerge(
          "mt-3 flex items-center gap-2 text-sm font-medium text-black-tint-10",
          readOnly && "text-black-tint-55",
        )}
      >
        <input
          checked={currentOrg.ai_assessment_enabled}
          disabled={readOnly}
          type="checkbox"
          onChange={handleAiAssessmentEnabledChange}
        />
        {t("home.settings.ai_assessment_enabled")}
      </label>

      <div className="flex items-center">
        <SquareButton
          className="mt-2"
          disabled={readOnly}
          eventName="Org Settings - Import course"
          kind="tertiary"
          size="s"
          onClick={() => sectionInput.current?.click()}
        >
          {t("home.settings.import_section")}
        </SquareButton>

        <input
          className="invisible w-0"
          disabled={readOnly}
          ref={sectionInput}
          type="file"
          onChange={(event) => {
            onImportSection(event).catch(toastLocalizedOperationCatcher("import_section_failure"));
          }}
        />
      </div>
    </div>
  );
};
